/**
 * The user's microphone choice, readable synchronously from anywhere.
 *
 * The capture counterpart of `dictation-engine-preference.ts`, and it exists
 * for the same reason: every request that opens the microphone - a recording,
 * live voice, the wake word - has to carry the device with it, and none of
 * those paths should await a render or load the settings store to find out
 * which one. The store owns persistence and pushes each change in here; the
 * capture code reads it back out. This module deliberately imports nothing.
 */

/** The chosen device id, or null while the microphone is Automatic. */
let inputDeviceId: string | null = null;
const listeners = new Set<() => void>();

export function setVoiceInputDevicePreference(next: string | null): void {
  const changed = (next || null) !== (inputDeviceId || null);
  inputDeviceId = next;
  if (changed) for (const listener of listeners) listener();
}

/** Called after each change of the chosen device, never for a re-publish of
 *  the same one. Returns the unsubscribe. */
export function subscribeVoiceInputDevicePreference(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The device argument to send with a request that opens the microphone.
 *
 * An EMPTY object while Automatic, never `{ inputDeviceId: undefined }`: the
 * native side reads a missing id as Automatic, so a request that says nothing
 * behaves exactly as it did before this choice existed.
 */
export function voiceInputArgs(): { inputDeviceId?: string } {
  return inputDeviceId ? { inputDeviceId } : {};
}
